<template>
  <Report
    :report-id="reportId"
    title="Channel Messages"
    tooltip-text="A trend showing the total sent and received messages for each channel."
    class="chart-container"
    :data-empty="isDataEmpty"
    :loaded-data="isDataLoaded"
    :loading-error="loadingError"
  >
    <template #chart>
      <BarChart :reportId="reportId" class="chart" :data="reportData" :seriesOrder="seriesOrder" />
    </template>
  </Report>
</template>

<script lang="ts" setup>
import BarChart from '@/features/reports/components/chartjs/BarChart.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import ChartError from '@/features/reports/components/ChartError.vue';
import ChartLabels from '@/features/reports/components/ChartLabels.vue';
import ChartNoData from '@/features/reports/components/ChartNoData.vue';
import HelpIcon from 'vue-material-design-icons/HelpCircleOutline.vue';
import { Data, SeriesConfig } from '@/features/reports/models';
import { nanoid } from 'nanoid';
import { computed, PropType, ref } from 'vue';
import { useReportData } from '@/features/reports/util/useReportData';
import Report from '@/features/reports/components/Report.vue';

const props = defineProps({
  data: {
    type: Object as PropType<Data>,
    required: true,
  },
  loadedData: {
    type: Boolean,
    default: false,
  },
  loadingError: {
    type: Boolean,
    default: false,
  },
});

const { reportId, isDataLoaded, isDataEmpty } = useReportData(props);

const seriesOrder = computed(() => {
  const channels: SeriesConfig[] = [
    { name: 'BT Smart Messaging Tailored', displayName: 'BT Smart Messaging Tailored', colour: '#5614B4' },
    { name: 'GOV.UK Notify SMS', displayName: 'GOV.UK Notify SMS', colour: '#000000' },
    { name: 'Instagram', displayName: 'Instagram', colour: '#E1306C' },
    { name: 'Whatsapp', displayName: 'WhatsApp', colour: '#0E3926' },
    { name: 'Messenger', displayName: 'Messenger', colour: '#006AFF' },
    { name: 'Web Assistant', displayName: 'Web Assistant', colour: '#062B80' },
  ];

  return channels.filter((c) => {
    return props.data.series.some((s) => s.name === c.name);
  });
});

const reportData = computed<Data>(() => ({
  scale: props.data.scale,
  series: props.data.series,
}));
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '@/features/reports/components/reports.scss' as *;
</style>
